export type DemoCommandOp =
  | "transport.play"
  | "transport.pause"
  | "transport.seek"
  | "transport.setRate"
  | "transport.setPitch"
  | "transport.setSong"
  | "transport.setLead"
  | "transport.setLatencyComp";

export type VisualEventType =
  | "join"
  | "download-progress"
  | "issue-play"
  | "issue-pause"
  | "issue-seek"
  | "issue-rate"
  | "issue-pitch"
  | "issue-song"
  | "issue-lead"
  | "issue-latency-comp"
  | "receive-play"
  | "receive-pause"
  | "receive-seek"
  | "receive-rate"
  | "receive-pitch"
  | "receive-song"
  | "receive-lead"
  | "receive-latency-comp"
  | "execute-play"
  | "execute-pause"
  | "execute-seek"
  | "execute-rate"
  | "execute-pitch"
  | "execute-song"
  | "execute-lead"
  | "execute-latency-comp"
  | "all-received";

export interface BenchPayload {
  readonly commandId: string;
  readonly seq: number;
  readonly op: DemoCommandOp;
  readonly issuedAtMs: number;
  readonly executeAtMs: number;
  readonly authoritativeExecuteAtMs?: number;
  readonly issuerId: string;
  readonly issuerCountry?: string;
  readonly toPositionSec?: number;
  readonly rate?: number;
  readonly pitchSemitones?: number;
  readonly leadMs?: number;
  readonly latencyCompEnabled?: boolean;
  readonly songYoutubeId?: string;
  readonly songTitle?: string;
  readonly songSmamsUrl?: string;
  readonly songStemUrlsByRole?: Readonly<Record<string, string>>;
}

export interface CommandEnvelopeLike {
  readonly op: unknown;
  readonly commandId?: unknown;
  readonly executeAtServerMonoMs?: unknown;
  readonly authoritativeExecuteAtMs?: unknown;
  readonly payload?: unknown;
}

export const DEFAULT_COUNTRY_CODES = [
  "AU",
  "JP",
  "SG",
  "DE",
  "GB",
  "US",
  "BR",
  "ZA",
  "IN",
  "CA",
  "KR",
  "FR",
  "NL",
  "ES",
  "IT",
  "MX",
  "AR",
  "CL",
  "NZ",
  "ID",
  "SE",
  "NO",
  "DK",
  "FI",
  "PL",
  "PT",
  "IE",
  "CH",
  "AT",
  "BE",
  "TR",
  "GR",
  "CZ",
  "HU",
  "RO",
  "BG",
  "HR",
  "SI",
  "SK",
  "EE",
  "LV",
  "LT",
  "IS",
  "LU",
  "MT",
  "CY",
  "TH",
  "VN",
  "MY",
  "PH",
  "AE",
  "SA",
  "IL",
  "EG",
  "NG",
  "KE",
  "GH",
  "CO",
  "PE",
  "UY",
  "EC",
  "PA",
  "CR",
  "DO",
  "TW",
  "HK",
];

export function safeNumber(value: unknown): number | null {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  return value;
}

export function parseBenchPayload(command: CommandEnvelopeLike): BenchPayload | null {
  const payload = command.payload;
  if (payload === null || payload === undefined || typeof payload !== "object") {
    return null;
  }
  const asRecord = payload as Record<string, unknown>;
  const seq = safeNumber(asRecord.seq);
  const issuedAtMs = safeNumber(asRecord.issuedAtMs);
  const executeAtMs = safeNumber(asRecord.executeAtMs);
  const payloadCommandId =
    typeof asRecord.commandId === "string" && asRecord.commandId.trim().length > 0
      ? asRecord.commandId.trim()
      : null;
  const envelopeCommandId =
    typeof command.commandId === "string" && command.commandId.trim().length > 0
      ? command.commandId.trim()
      : null;
  const authoritativeExecuteAtMs =
    safeNumber(asRecord.authoritativeExecuteAtMs) ??
    safeNumber(asRecord.executeAtServerMonoMs) ??
    safeNumber(command.authoritativeExecuteAtMs) ??
    safeNumber(command.executeAtServerMonoMs);
  const issuerId = typeof asRecord.issuerId === "string" ? asRecord.issuerId : null;
  const op = isDemoCommandOp(command.op) ? command.op : null;
  if (seq === null || issuedAtMs === null || executeAtMs === null || !issuerId || !op) {
    return null;
  }
  const commandId = payloadCommandId ?? envelopeCommandId ?? `${issuerId}:${seq}`;
  if (op === "transport.seek") {
    const toPositionSec = safeNumber(asRecord.toPositionSec);
    if (toPositionSec === null) {
      return null;
    }
    return {
      commandId,
      seq,
      op,
      issuedAtMs,
      executeAtMs,
      ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
      issuerId,
      toPositionSec,
    };
  }
  if (op === "transport.setRate") {
    const rate = safeNumber(asRecord.rate);
    if (rate === null) {
      return null;
    }
    return {
      commandId,
      seq,
      op,
      issuedAtMs,
      executeAtMs,
      ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
      issuerId,
      rate,
    };
  }
  if (op === "transport.setPitch") {
    const pitchSemitones = safeNumber(asRecord.pitchSemitones);
    if (pitchSemitones === null) {
      return null;
    }
    return {
      commandId,
      seq,
      op,
      issuedAtMs,
      executeAtMs,
      ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
      issuerId,
      pitchSemitones,
    };
  }
  if (op === "transport.setSong") {
    const songYoutubeId =
      typeof asRecord.songYoutubeId === "string" ? asRecord.songYoutubeId.trim() : "";
    const songTitle =
      typeof asRecord.songTitle === "string" ? asRecord.songTitle.trim() : undefined;
    const songSmamsUrl =
      typeof asRecord.songSmamsUrl === "string" ? asRecord.songSmamsUrl.trim() : undefined;
    let songStemUrlsByRole: Record<string, string> | undefined;
    const stemUrlsRaw = asRecord.songStemUrlsByRole;
    if (stemUrlsRaw !== null && stemUrlsRaw !== undefined && typeof stemUrlsRaw === "object") {
      const parsedStems: Record<string, string> = {};
      for (const [role, maybeUrl] of Object.entries(stemUrlsRaw as Record<string, unknown>)) {
        if (typeof maybeUrl !== "string") {
          continue;
        }
        const trimmedRole = role.trim();
        const trimmedUrl = maybeUrl.trim();
        if (!trimmedRole || !trimmedUrl) {
          continue;
        }
        parsedStems[trimmedRole] = trimmedUrl;
      }
      if (Object.keys(parsedStems).length > 0) {
        songStemUrlsByRole = parsedStems;
      }
    }
    if (!songYoutubeId) {
      return null;
    }
    return {
      commandId,
      seq,
      op,
      issuedAtMs,
      executeAtMs,
      ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
      issuerId,
      songYoutubeId,
      ...(songTitle ? { songTitle } : {}),
      ...(songSmamsUrl ? { songSmamsUrl } : {}),
      ...(songStemUrlsByRole ? { songStemUrlsByRole } : {}),
    };
  }
  if (op === "transport.setLead") {
    const leadMs = safeNumber(asRecord.leadMs);
    if (leadMs === null) {
      return null;
    }
    return {
      commandId,
      seq,
      op,
      issuedAtMs,
      executeAtMs,
      ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
      issuerId,
      leadMs,
    };
  }
  if (op === "transport.setLatencyComp") {
    const latencyCompEnabled = asRecord.latencyCompEnabled;
    if (typeof latencyCompEnabled !== "boolean") {
      return null;
    }
    return {
      commandId,
      seq,
      op,
      issuedAtMs,
      executeAtMs,
      ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
      issuerId,
      latencyCompEnabled,
    };
  }
  return {
    commandId,
    seq,
    op,
    issuedAtMs,
    executeAtMs,
    ...(authoritativeExecuteAtMs !== null ? { authoritativeExecuteAtMs } : {}),
    issuerId,
  };
}

export function isDemoCommandOp(value: unknown): value is DemoCommandOp {
  return (
    value === "transport.play" ||
    value === "transport.pause" ||
    value === "transport.seek" ||
    value === "transport.setRate" ||
    value === "transport.setPitch" ||
    value === "transport.setSong" ||
    value === "transport.setLead" ||
    value === "transport.setLatencyComp"
  );
}

export function issueEventType(op: DemoCommandOp): VisualEventType {
  switch (op) {
    case "transport.play":
      return "issue-play";
    case "transport.pause":
      return "issue-pause";
    case "transport.seek":
      return "issue-seek";
    case "transport.setRate":
      return "issue-rate";
    case "transport.setPitch":
      return "issue-pitch";
    case "transport.setSong":
      return "issue-song";
    case "transport.setLead":
      return "issue-lead";
    case "transport.setLatencyComp":
      return "issue-latency-comp";
  }
}

export function receiveEventType(op: DemoCommandOp): VisualEventType {
  switch (op) {
    case "transport.play":
      return "receive-play";
    case "transport.pause":
      return "receive-pause";
    case "transport.seek":
      return "receive-seek";
    case "transport.setRate":
      return "receive-rate";
    case "transport.setPitch":
      return "receive-pitch";
    case "transport.setSong":
      return "receive-song";
    case "transport.setLead":
      return "receive-lead";
    case "transport.setLatencyComp":
      return "receive-latency-comp";
  }
}

export function executeEventType(op: DemoCommandOp): VisualEventType {
  switch (op) {
    case "transport.play":
      return "execute-play";
    case "transport.pause":
      return "execute-pause";
    case "transport.seek":
      return "execute-seek";
    case "transport.setRate":
      return "execute-rate";
    case "transport.setPitch":
      return "execute-pitch";
    case "transport.setSong":
      return "execute-song";
    case "transport.setLead":
      return "execute-lead";
    case "transport.setLatencyComp":
      return "execute-latency-comp";
  }
}

export function normalizeCountryCode(raw: string): string | null {
  const code = raw.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) {
    return null;
  }
  return code;
}

export function parseCountryCodes(csv: string): string[] {
  const seen = new Set<string>();
  const parsed: string[] = [];
  for (const part of csv.split(",")) {
    const code = normalizeCountryCode(part);
    if (!code || seen.has(code)) {
      continue;
    }
    seen.add(code);
    parsed.push(code);
  }
  return parsed;
}

export function buildCountryList(requestedCount: number, csv: string): string[] {
  const provided = parseCountryCodes(csv);
  const seen = new Set(provided);
  const combined = [...provided];
  for (const fallback of DEFAULT_COUNTRY_CODES) {
    if (combined.length >= requestedCount) {
      break;
    }
    if (seen.has(fallback)) {
      continue;
    }
    combined.push(fallback);
    seen.add(fallback);
  }
  return combined.slice(0, requestedCount);
}

export function countryCodeToFlagEmoji(countryCode: string): string {
  const code = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) {
    return "🏳️";
  }
  const base = 127397;
  const chars = [
    String.fromCodePoint(base + code.charCodeAt(0)),
    String.fromCodePoint(base + code.charCodeAt(1)),
  ];
  return chars.join("");
}

export function summarizeJoinMs(values: number[]): {
  readonly count: number;
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  readonly p95: number;
} {
  if (!values.length) {
    return { count: 0, min: 0, max: 0, mean: 0, p95: 0 };
  }
  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((acc, value) => acc + value, 0);
  const p95Index = Math.floor((sorted.length - 1) * 0.95);
  return {
    count: sorted.length,
    min: sorted[0] ?? 0,
    max: sorted[sorted.length - 1] ?? 0,
    mean: sum / sorted.length,
    p95: sorted[p95Index] ?? 0,
  };
}

const MIX_PAYLOAD_KEYS = new Set([
  "stemVolumes",
  "stemVolumeByRole",
  "stems",
  "mix",
  "mixPreset",
  "volume",
  "muted",
  "solo",
]);

export function hasMixPayloadKeys(payload: Readonly<Record<string, unknown>>): boolean {
  for (const key of Object.keys(payload)) {
    if (MIX_PAYLOAD_KEYS.has(key)) {
      return true;
    }
  }
  return false;
}

export function eventColor(type: VisualEventType): string {
  switch (type) {
    case "join":
      return "#00ff66";
    case "download-progress":
      return "#39ff72";
    case "issue-play":
      return "#00ff00";
    case "issue-pause":
      return "#ff4040";
    case "issue-seek":
      return "#4dc2ff";
    case "issue-rate":
      return "#9d7bff";
    case "issue-pitch":
      return "#58ffd1";
    case "issue-song":
      return "#00d4ff";
    case "issue-lead":
      return "#ffa85c";
    case "issue-latency-comp":
      return "#80ff80";
    case "receive-play":
    case "receive-pause":
    case "receive-seek":
    case "receive-rate":
    case "receive-pitch":
    case "receive-song":
    case "receive-lead":
    case "receive-latency-comp":
      return "#ffd84d";
    case "execute-play":
      return "#39ffb6";
    case "execute-pause":
      return "#ff6a00";
    case "execute-seek":
      return "#48d7ff";
    case "execute-rate":
      return "#c59cff";
    case "execute-pitch":
      return "#4fffc7";
    case "execute-song":
      return "#7de2ff";
    case "execute-lead":
      return "#ffc783";
    case "execute-latency-comp":
      return "#afffaf";
    case "all-received":
      return "#ffe300";
    default:
      return "#ffffff";
  }
}
